import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import backendUrl from '../config';

export interface UserStats {
  id: number;
  email: string;
  username: string;
  role: 'admin' | 'user' | 'superadmin' | 'anis-admin' | 'anis-user' | 'cp-admin' | 'cp-user';
  azure_roles?: string[];  // All Azure AD roles/groups the user belongs to
  is_active: boolean;
  created_at: string;
  last_login?: string;
  summary_count: number;
  query_count: number;
}

export interface MessageRating {
  id: string;
  message_id: string;
  user_id: number;
  rating_type: 'thumbs' | 'stars';
  rating_value: number;
  feedback_text?: string;
  status: string;
  created_at: string;
  user?: UserStats;
}

export interface ChatSession {
  id: string;
  title?: string | null;
  user_id: number;
  source?: string;
  created_at: string;
  messages: { id: string; sender: string; content: string; created_at: string; sequence_number?: number }[];
}

export interface AnalyticsData {
  scraping_success_rate: {
    static: number;
    dynamic: number;
  };
  pipeline_latency: {
    average: number;
    min: number;
    max: number;
  };
  job_failures: {
    total: number;
    rate: number;
    recent_exceptions: string[];
  };
  active_users: {
    daily: number;
    weekly: number;
    monthly: number;
  };
  qa_usage: {
    queries_per_user: number;
    queries_per_session: number;
    top_queries: Array<{
      query: string;
      count: number;
      similarity_score: number;
    }>;
    qa_vs_digest_percentage: number;
  };
}

export type FeedbackSortField = 'created_at' | 'rating' | 'status' | 'category' | 'user_id' | 'comment';
export type SortDir = 'asc' | 'desc';

export interface FetchFeedbackParams {
  skip?: number;
  limit?: number;
  sortField?: FeedbackSortField;
  sortDir?: SortDir;
}

interface AdminState {
  users: UserStats[];
  analytics: AnalyticsData | null;
  feedback: any[];
  feedbackTotal: number;
  messageRatings: MessageRating[];
  chatSessions: ChatSession[];
  chatSessionsLoading: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: AdminState = {
  users: [],
  analytics: null,
  feedback: [],
  feedbackTotal: 0,
  messageRatings: [],
  chatSessions: [],
  chatSessionsLoading: false,
  loading: false,
  error: null,
};

// Async thunks
export const fetchAllUsers = createAsyncThunk(
  'admin/fetchUsers',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState() as { auth: { token: string } };
      const response = await axios.get(`${backendUrl}/api/admin/users`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch users');
    }
  }
);

export const fetchAnalytics = createAsyncThunk(
  'admin/fetchAnalytics',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState() as { auth: { token: string } };
      const response = await axios.get(`${backendUrl}/api/admin/analytics`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch analytics');
    }
  }
);

export const updateUserRole = createAsyncThunk(
  'admin/updateUserRole',
  async ({ userId, role }: { userId: string; role: 'admin' | 'user' | 'superadmin' | 'anis-admin' | 'anis-user' }, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState() as { auth: { token: string } };
      const response = await axios.put(`${backendUrl}/api/admin/users/${userId}/role`, { role }, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to update user role');
    }
  }
);

export const fetchAllFeedback = createAsyncThunk(
  'admin/fetchAllFeedback',
  async (params: FetchFeedbackParams | undefined, { getState, rejectWithValue, signal }) => {
    try {
      const { auth } = getState() as { auth: { token: string } };
      const { skip = 0, limit = 25, sortField = 'created_at', sortDir = 'desc' } = params || {};
      const response = await axios.get(`${backendUrl}/api/admin/feedback`, {
        params: { skip, limit, sort_field: sortField, sort_dir: sortDir },
        headers: { Authorization: `Bearer ${auth.token}` },
        signal,
      });
      const total = Number(response.headers['x-total-count']) || 0;
      return { items: response.data, total };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch feedback');
    }
  }
);

export const updateFeedbackStatus = createAsyncThunk(
  'admin/updateFeedbackStatus',
  async (
    {
      feedbackId,
      status,
    }: { feedbackId: string; status: 'pending' | 'reviewed' | 'implemented' },
    { getState, rejectWithValue }
  ) => {
    try {
      const { auth } = getState() as { auth: { token: string } };
      const response = await axios.put(`${backendUrl}/api/admin/feedback/${feedbackId}/status`, { status }, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to update feedback status');
    }
  }
);

export const deleteFeedback = createAsyncThunk(
  'admin/deleteFeedback',
  async ({ feedbackId }: { feedbackId: string }, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState() as { auth: { token: string } };
      await axios.delete(`${backendUrl}/api/admin/feedback/${feedbackId}`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      return { id: feedbackId };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to delete feedback');
    }
  }
);

export const updateRatingStatus = createAsyncThunk(
  'admin/updateRatingStatus',
  async ({ ratingId, status }: { ratingId: string; status: 'pending' | 'reviewed' }, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState() as { auth: { token: string } };
      const response = await axios.put(`${backendUrl}/api/admin/ratings/${ratingId}/status`, { status }, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to update rating status');
    }
  }
);

export const fetchAllRatings = createAsyncThunk(
  'admin/fetchAllRatings',
  async (userId: number | undefined, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState() as { auth: { token: string } };
      const url = userId ? `${backendUrl}/api/admin/ratings?user_id=${userId}` : `${backendUrl}/api/admin/ratings`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch message ratings');
    }
  }
);

export const fetchAllChats = createAsyncThunk(
  'admin/fetchAllChats',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState() as { auth: { token: string } };
      const response = await axios.get(`${backendUrl}/api/admin/chats`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch chat sessions');
    }
  }
);

export const fetchChatRatings = createAsyncThunk(
  'admin/fetchChatRatings',
  async (sessionId: string, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState() as { auth: { token: string } };
      const response = await axios.get(`${backendUrl}/api/admin/chats/${sessionId}/ratings`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch chat ratings');
    }
  }
);

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Users
      .addCase(fetchAllUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload;
      })
      .addCase(fetchAllUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch Analytics
      .addCase(fetchAnalytics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.analytics = action.payload;
      })
      .addCase(fetchAnalytics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update User Role
      .addCase(updateUserRole.fulfilled, (state, action) => {
        const index = state.users.findIndex(user => user.id === action.payload.id);
        if (index !== -1) {
          state.users[index] = action.payload;
        }
      })
      // Fetch All Feedback (admin) — server-side paginated + sorted
      .addCase(fetchAllFeedback.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllFeedback.fulfilled, (state, action) => {
        state.loading = false;
        state.feedback = action.payload.items;
        state.feedbackTotal = action.payload.total;
      })
      .addCase(fetchAllFeedback.rejected, (state, action) => {
        state.loading = false;
        // Aborted requests fire `rejected` with meta.aborted = true — suppress
        // those so a deliberately-cancelled fetch (e.g. rapid sort change)
        // doesn't surface as an error in the UI.
        if (action.meta.aborted) return;
        state.error = action.payload as string;
      })
      // Update Feedback Status
      .addCase(updateFeedbackStatus.fulfilled, (state, action) => {
        const idx = state.feedback.findIndex((f: any) => f.id === action.payload.id);
        if (idx !== -1) {
          state.feedback[idx] = action.payload;
        }
      })
      // Delete Feedback — drop locally, decrement total (server will reflect on next refetch)
      .addCase(deleteFeedback.fulfilled, (state, action) => {
        const id = action.payload?.id;
        if (!id) return;
        const before = state.feedback?.length || 0;
        state.feedback = (state.feedback || []).filter((f: any) => f.id !== id);
        const removed = before - state.feedback.length;
        state.feedbackTotal = Math.max(0, state.feedbackTotal - removed);
      })
      // Update Rating Status
      .addCase(updateRatingStatus.fulfilled, (state, action) => {
        const idx = state.messageRatings.findIndex((r: MessageRating) => r.id === action.payload.id);
        if (idx !== -1) {
          state.messageRatings[idx] = action.payload;
        }
      })
      // Fetch All Ratings
      .addCase(fetchAllRatings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllRatings.fulfilled, (state, action) => {
        state.loading = false;
        state.messageRatings = action.payload;
      })
      .addCase(fetchAllRatings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch All Chats
      .addCase(fetchAllChats.pending, (state) => {
        state.chatSessionsLoading = true;
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllChats.fulfilled, (state, action) => {
        state.chatSessionsLoading = false;
        state.loading = false;
        state.chatSessions = action.payload;
      })
      .addCase(fetchAllChats.rejected, (state, action) => {
        state.chatSessionsLoading = false;
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch Chat Ratings
      .addCase(fetchChatRatings.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchChatRatings.fulfilled, (state, action) => {
        state.loading = false;
        state.messageRatings = action.payload;
      })
      .addCase(fetchChatRatings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = adminSlice.actions;
export default adminSlice.reducer;
